import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// A badge is one of two things, and the prop you reach for says which.
//
//   variant — a chip that names something: "Docker", "Postgres", a project count, a hostname.
//             Squared off, an icon is normal, and the colour carries no meaning.
//   tone    — a label that means something: "Experimental", "Active", "Error". A pill tinted with
//             the status colours from pages/global.css, where the colour is the message.
//
// The two are mutually exclusive in the type below, so a call site cannot ask for both and leave
// the next reader guessing which one won. `size` applies to tones only — chips keep the single
// shape they have always had, so adding this prop changed none of them.
const badgeVariants = cva(
  "inline-flex items-center justify-center w-fit shrink-0 whitespace-nowrap gap-1 text-xs font-medium overflow-hidden [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow]",
  {
    variants: {
      variant: {
        default:
          "border border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90",
        secondary:
          "border border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        destructive:
          "border border-transparent bg-destructive text-white [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline: "border text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground"
      },
      tone: {
        success: "bg-success-bg text-success-text",
        warning: "bg-warning-bg text-warning-text",
        destructive: "bg-destructive-bg text-destructive-text",
        info: "bg-info-bg text-info-text",
        muted: "bg-neutral-bg text-neutral-text"
      },
      // `chip` is the shape every existing badge already had. The two pill steps are for tones:
      // `sm` for table rows, where a 24px badge pushes the line box open, `md` everywhere else.
      shape: {
        chip: "rounded-md px-2 py-0.5 [&>svg]:size-3",
        pillSm: "rounded-full tabular-nums h-5 px-2 text-[11px] leading-5 tracking-[0.2px] [&>svg]:size-3",
        pillMd: "rounded-full tabular-nums h-6 px-3 leading-6 [&>svg]:size-3.5"
      }
    },
    defaultVariants: {
      shape: "chip"
    }
  }
);

export type BadgeTone = NonNullable<VariantProps<typeof badgeVariants>["tone"]>;
export type BadgeSize = "sm" | "md";

type BadgeStyle =
  | { variant?: VariantProps<typeof badgeVariants>["variant"]; tone?: never; size?: never }
  | { tone: BadgeTone; size?: BadgeSize; variant?: never };

export const Badge = ({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"span"> & BadgeStyle & { asChild?: boolean }) => {
  const Comp = asChild ? Slot : "span";
  const { variant, tone, size, ...rest } = props as {
    variant?: VariantProps<typeof badgeVariants>["variant"];
    tone?: BadgeTone;
    size?: BadgeSize;
  } & React.ComponentProps<"span">;

  return (
    <Comp
      data-slot="badge"
      data-tone={tone}
      className={cn(
        badgeVariants({
          variant: tone ? undefined : (variant ?? "default"),
          tone,
          shape: tone ? (size === "sm" ? "pillSm" : "pillMd") : "chip"
        }),
        className
      )}
      {...rest}
    />
  );
};

export { badgeVariants };
